import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    useTheme,
    useMediaQuery,
    ThemeProvider
} from '@mui/material';
import Validate from '../../../../app/validation/validation';
import { TextInputHot, MODE } from 'components/common/TextInputHot';
import { ButtonHot } from 'components/common/ButtonHot';
import {
    setIsFirstRegistrationNeeded,
    setUserIdLogin,
    setLoginFormName,
    setLoginId,
    setLoginCode,
} from '../../../../redux/actions';
import { useIsMemberMutation } from '../../../../redux/api/users.api';
import Recaptcha from '../../../../components/services/google/recaptcha';
import { executeWithCaptcha, generateTestId } from '../../../../app/functions';
import { RECAPTCHA_ACTIONS } from 'components/services/google/recaptcha';
import { appTheme } from 'common/theme';
import AnalyticsManager from 'app/analytics';

const IdLoginForm = ({ text, testID = 'id-login-form' }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
    
    const loginCode = useSelector((state) => state.data.loginCode);
    const loginId = useSelector((state) => state.data.userLoginId);
    const [isMemberMutation, { isLoading: isMutationLoading }] = useIsMemberMutation();
    const idInputRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [firstTry, setFirstTry] = useState(true);
    const [form, setForm] = useState({
        id: {
            val: '',
            valid: false,
            rules: ['not_empty', 'id', 'not_empty'],
            errMsg: '',
            placeholder: text?.['id_text_welcome_screen'] || 'הזן מספר זהות',
            type: 'number',
            pattern: '[0-9]*',
        },
    });

    const handleInputChange = (evt) => {
        const { name, value } = evt.target;
        let validate = Validate(value, form[name].rules);
        let is_valid = validate.valid;

        dispatch(setLoginId(value));
        dispatch(setLoginCode(0));
        setFirstTry(true);
        setForm((prevForm) => ({
            ...prevForm,
            [name]: { ...prevForm[name], val: value, valid: is_valid, errMsg: '' },
        }));
    };

    useEffect(() => {
        setIsLoading(false);
        if (loginCode && loginCode === 163) {
            dispatch(setIsFirstRegistrationNeeded(false));
            dispatch(setLoginFormName('login_form'));
        } else if (loginCode && loginCode === 4006) {
            dispatch(setIsFirstRegistrationNeeded(true));
            dispatch(setLoginFormName('registration_form'));
        } else if (loginCode && loginCode === 180) {
            setForm(prevForm => ({
                ...prevForm,
                id: {
                    ...prevForm.id,
                    valid: false,
                    errMsg: text?.['user_not_exists'] || 'משתמש לא קיים במערכת'
                }
            }));
        }
    }, [loginCode, dispatch, text]);

    useEffect(() => {
        if (idInputRef.current) {
            const inputElement = idInputRef.current.querySelector('input');
            if (inputElement) {
                inputElement.focus();
            }
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isLoading || isMutationLoading) {
            return;
        }

        let form_valid = true;
        let new_form = { ...form };

        for (let field in form) {
            let validate = Validate(form[field].val, form[field].rules);
            let is_valid = validate.valid;
            let msg = validate.msg;

            new_form[field].valid = is_valid;
            new_form[field].errMsg = msg;

            if (is_valid === false) {
                form_valid = false;
            }
        }

        setForm(new_form);
        setFirstTry(false);

        if (form_valid) {
            setIsLoading(true);
            executeWithCaptcha(async (token) => {
                let payload = {};
                payload['g-recaptcha-response'] = token;
                payload['userId'] = form.id.val;
                payload['id'] = form.id.val;
                
                try {
                    const result = await isMemberMutation(payload).unwrap();
                    dispatch(setLoginCode(result.loginCode));
                    dispatch(setUserIdLogin(Number(payload.userId)));
                } catch (error) {
                    dispatch(setLoginCode(error.id || 180));
                    AnalyticsManager.logLoginFailed();
                } finally {
                    setIsLoading(false);
                }
            }, RECAPTCHA_ACTIONS.VALIDATE_USER);
        }
    };

    const id = form.id;
    const hasError = !firstTry && !id.valid;

    return (
        <ThemeProvider theme={appTheme}>
            <Box 
                component="form" 
                onSubmit={handleSubmit} 
                testID={generateTestId(testID, 'form')}
                sx={{ 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <TextInputHot
                    ref={idInputRef}
                    name="id"
                    mode={MODE.FORM}
                    label={text?.['login_id_label'] || 'תעודת זהות'}
                    placeholder={text?.['id_text_welcome_screen'] || 'הזן מספר זהות'}
                    value={loginId || id.val}
                    onChange={handleInputChange}
                    error={hasError}
                    helperText={hasError ? id.errMsg : ''}
                    testID={generateTestId(testID, 'id-input')}
                />

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <ButtonHot
                        type="submit"
                        mode="contained"
                        size={isMobile ? 'l' : 'xl'}
                        disabled={isLoading || isMutationLoading}
                        loading={isLoading || isMutationLoading}
                        testID={generateTestId(testID, 'submit-button')}
                    >
                        {text?.['login_log_in'] || 'חברו'}
                    </ButtonHot>
                </Box>

                <Box sx={{ mt: 2 }} testID={generateTestId(testID, 'recaptcha-container')}>
                    <Recaptcha isFunction={true} enterprise={true} onReady={() => {}} />
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default IdLoginForm;
